/**
 * Alert — vendored from shadcn/ui and restyled as the §7.5 band (NFR-008).
 *
 * Variants are the four functional bands: info (default), warning, blocked and
 * success — fill 50, border 200, icon 700 of the family, through the `--color-*`
 * tokens (the functional families are tokens, not `@theme` utilities). Radius 12,
 * padding 12px 16px, 14/400 navy-950 text. The `blocked` band is `role="alert"`;
 * the others are a polite `role="status"`.
 *
 * The disclaimer band (T.2, T.5, SAT.7) is NOT a variant here: it lives in
 * components/trellis/DisclaimerBand.tsx because it is a compliance surface.
 */
import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from './cn'

const alertVariants = cva(
  'relative flex w-full gap-3 rounded-lg border px-4 py-3 text-body text-navy-950 [&>svg]:mt-0.5 [&>svg]:size-5 [&>svg]:shrink-0',
  {
    variants: {
      variant: {
        info: 'border-[var(--color-info-200)] bg-[var(--color-info-50)] [&>svg]:text-[var(--color-info-700)]',
        warning:
          'border-[var(--color-warning-200)] bg-[var(--color-warning-50)] [&>svg]:text-[var(--color-warning-700)]',
        blocked:
          'border-[var(--color-error-200)] bg-[var(--color-error-50)] [&>svg]:text-[var(--color-error-700)]',
        success:
          'border-[var(--color-success-200)] bg-[var(--color-success-50)] [&>svg]:text-[var(--color-success-700)]',
      },
    },
    defaultVariants: { variant: 'info' },
  },
)

export interface AlertProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof alertVariants> {}

export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({ className, variant, role, ...props }, ref) => (
    <div
      ref={ref}
      role={role ?? (variant === 'blocked' ? 'alert' : 'status')}
      className={cn(alertVariants({ variant, className }))}
      {...props}
    />
  ),
)
Alert.displayName = 'Alert'

export const AlertTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn('font-medium leading-5', className)} {...props} />
  ),
)
AlertTitle.displayName = 'AlertTitle'

export const AlertDescription = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('text-body [&_p]:leading-5', className)} {...props} />
  ),
)
AlertDescription.displayName = 'AlertDescription'
